import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import useThemeStyles from '../../../hooks/useThemeStyles';

const ReviewStep = ({ preview, formattingOptions, aiOptions }) => {
  const { colors } = useThemeStyles();

  if (!preview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const aiExtractionLabels = aiOptions.enabled
    ? [
        aiOptions.characters && 'Characters',
        aiOptions.timeline && 'Timeline events',
        aiOptions.relationships && 'Family relationships',
      ].filter(Boolean)
    : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          p: 2,
          backgroundColor: colors.paper.backgroundSecondary,
          borderRadius: 1,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            File Name
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.primary }}>
            {preview.fileName}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            File Size
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.primary }}>
            {(preview.fileSize / 1024).toFixed(1)} KB
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Chapters Detected
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.primary }}>
            {preview.chaptersDetected}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Total Words
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.primary }}>
            {preview.totalWordCount?.toLocaleString() || 0}
          </Typography>
        </Box>
      </Box>

      {/* Manuscript Breakdown */}
      {preview.breakdown && (
        <>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: colors.text.primary }}>
            Manuscript Breakdown:
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 2,
              p: 2,
              backgroundColor: colors.paper.backgroundSecondary,
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: colors.primary.main }}>
                {preview.breakdown.chapters || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Chapters
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: colors.primary.main }}>
                {preview.breakdown.scenes || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Scenes
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: colors.primary.main }}>
                {preview.breakdown.totalWords?.toLocaleString() || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Total Words
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <Divider />

      <Typography variant="subtitle2" sx={{ color: colors.text.primary }}>
        Chapters to be created:
      </Typography>
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {preview.chapters?.map((chapter, chapterIndex) => (
          <Box
            key={chapter.name || chapter.id || chapterIndex}
            sx={{
              mb: 1,
              border: `1px solid ${colors.border.subtle}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: colors.paper.backgroundSecondary,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'medium', color: colors.text.primary }}
                >
                  {chapter.index || chapterIndex + 1}. {chapter.name}
                </Typography>
              </Box>
              <Chip
                label={`${chapter.wordCount?.toLocaleString() || 0} words`}
                size="small"
                variant="outlined"
                color="primary"
              />
            </Box>
            {chapter.scenes && chapter.scenes.length > 0 && (
              <Box sx={{ pl: 2, pr: 1, pb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.5, color: colors.text.secondary }}
                >
                  Scenes ({chapter.scenes.length}):
                </Typography>
                {chapter.scenes.map((scene, sceneIndex) => (
                  <Box
                    key={scene.name || sceneIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      pl: 2,
                      borderLeft: `2px solid ${colors.border.subtle}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.75rem', color: colors.text.primary }}
                    >
                      • {scene.name || `Scene ${scene.index || sceneIndex + 1}`}
                    </Typography>
                    {scene.wordCount > 0 && (
                      <Chip
                        label={`${scene.wordCount.toLocaleString()} words`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {preview.warnings?.length > 0 && (
        <Alert severity="warning">
          {preview.warnings.length} warning(s) during parsing. Some content may not import
          perfectly.
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mt: 1, color: colors.text.primary }}>
        Formatting to preserve:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {Object.entries(formattingOptions)
          .filter(([, value]) => value)
          .map(([key]) => (
            <Chip key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} size="small" />
          ))}
      </Box>

      {aiExtractionLabels.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ color: colors.text.primary }}>
            AI analysis after import:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {aiExtractionLabels.map((label) => (
              <Chip key={label} label={label} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

ReviewStep.propTypes = {
  preview: PropTypes.shape({
    fileName: PropTypes.string,
    fileSize: PropTypes.number,
    chaptersDetected: PropTypes.number,
    totalWordCount: PropTypes.number,
    chapters: PropTypes.arrayOf(PropTypes.shape({})),
    warnings: PropTypes.arrayOf(PropTypes.string),
    breakdown: PropTypes.shape({
      chapters: PropTypes.number,
      scenes: PropTypes.number,
      totalWords: PropTypes.number,
    }),
  }),
  formattingOptions: PropTypes.shape({}).isRequired,
  aiOptions: PropTypes.shape({
    enabled: PropTypes.bool,
    characters: PropTypes.bool,
    timeline: PropTypes.bool,
    relationships: PropTypes.bool,
  }).isRequired,
};

ReviewStep.defaultProps = {
  preview: null,
};

export default ReviewStep;
